import React from 'react';
import { useSelector } from 'react-redux';
import { Hidden } from '@material-ui/core';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import GetApp from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import PrintIcon from '@material-ui/icons/Print';
import Banner from '@cpg-its/ads.containers.banner';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { BeneficiaryLandingPageModel, preretirement, cpfLife, layLumpSum } from './model/beneficiary-models';
import { BeneficiarySettings } from './model/epi-props';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import BeneficiaryPlanView from './beneficiary-plan-view';
import { convertSpouseToBeneficiary } from './helper/beneficiary-helper';
import { labelSupplementStyles } from '../../../../global/constants/theme/styles/components/label-supplement-styles';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { TcsmStates } from '../../../../root/tcsm-model';
import { YesNoIndicators } from '../../../../global/models/api-model';
import { getFullLetterMonthDateTime } from '../../../../global/helpers/date-helpers';

interface Props {
    settings: BeneficiarySettings;
    beneficiaryLandingPageModel: BeneficiaryLandingPageModel;
}
function BeneficiaryLandingPageView(props: Props) {

    const { settings, beneficiaryLandingPageModel } = props;
    const gpsItem = useGpsType(GpsType.BENEFICIARY_LANDINGPAGE);
	const isClergy = useSelector<TcsmStates, boolean>(state => state?.personalInfo?.myCPG?.currentPerson?.is_clergy === YesNoIndicators.Yes);
	const isLay = !isClergy;	

    const preretirmentData = beneficiaryLandingPageModel?.beneficiaryData && beneficiaryLandingPageModel?.beneficiaryData[preretirement];

    const labelSupplementClasses = labelSupplementStyles();
    const buttonClasses = buttonStyles();
    const handlePrint = () => {

        window.print();
    };

	function RenderLastUpdate(){
		if (!beneficiaryLandingPageModel?.beneficiaryData?.date_last_updated) return null;

		return (
			<ContentSpacer>
				<div> {settings?.lastUpdatedLabel} {getFullLetterMonthDateTime(beneficiaryLandingPageModel?.beneficiaryData?.date_last_updated)} </div>
			</ContentSpacer>			
		);
	}

	function RenderPreretirment(){
		if (!isClergy) return null;

		if (!preretirmentData){
			return (<WysiwygWithReactLink content={settings?.noDbPlanMessage} />);
		}

		return (
			<BeneficiaryPlanView
						planName={settings?.dbPlanName}
						description={settings?.dbPlanDescription}
						beneficiaryPlan={preretirmentData}
						settings={settings}
						defaultSpouse={convertSpouseToBeneficiary(beneficiaryLandingPageModel?.beneficiaryData?.default_spouse)}
						plan={preretirement}
					/>			
		);
	}

	function RenderCpfLife(){
		if (!isClergy) return null;

		if (beneficiaryLandingPageModel?.beneficiaryData && beneficiaryLandingPageModel?.beneficiaryData[cpfLife]){
			return (
				<BeneficiaryPlanView
                                planName={settings?.cpfPlanName}
                                description={settings?.cpfPlanDescription}
                                beneficiaryPlan={beneficiaryLandingPageModel?.beneficiaryData[cpfLife]}
                                settings={settings}
                                plan={cpfLife}
                            />			
			);
		}
		
		return (<WysiwygWithReactLink content={settings?.noCpfPlanMessage} />);
	}

	function RenderLayLumpSum(){
		if (!isLay) return null;

		if (beneficiaryLandingPageModel?.beneficiaryData && beneficiaryLandingPageModel?.beneficiaryData[layLumpSum]){
			return (
				<BeneficiaryPlanView
									planName={settings?.layLumpSumPlanName}
									description={settings?.layLumpSumPlanDescription}
									beneficiaryPlan={beneficiaryLandingPageModel?.beneficiaryData[layLumpSum]}
									settings={settings}
									plan={layLumpSum}
								/>			
			);
		}
		
		return (<WysiwygWithReactLink content={settings?.noLayLumpSumPlanMessage} />);
	}

    return (
        <PageContainer>
            <MainContent>
                {
                    beneficiaryLandingPageModel?.showMessage &&
                    <ContentSpacer>
                        <Banner banner={{
                            type: 'success',
                            label: <WysiwygWithReactLink content={settings?.changeSavedMessage} />,
                        }} />
                    </ContentSpacer>
                }
                <ContentSpacer>
                    <div className={`${labelSupplementClasses.labelLayout} full-width`}>
                        <h1>{settings?.beneficiaryLandingPageTitle}</h1>
                        <div className='supplement-wrapper'>
                            <Hidden smDown>
                                <button className={buttonClasses.linkButton} onClick={handlePrint} type='button'><PrintIcon />{settings?.printerLinkText}</button>
                            </Hidden>
                        </div>
                    </div>
                </ContentSpacer>
                <ContentSpacer>
                    <WysiwygWithReactLink content={settings?.beneficiaryLandingPageDescription} />
                </ContentSpacer>
                <Hidden mdUp>
                    <ContentSpacer>
                        <button className={buttonClasses.linkButton} onClick={handlePrint} type='button'><PrintIcon /><span>{settings?.printerLinkText}</span></button>
                    </ContentSpacer>
                </Hidden>
                <Hidden mdUp>
                    <SideBarContent>
                        <div className='sidebar'>
                            <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                        </div>
                    </SideBarContent>
                </Hidden>

				{ RenderLastUpdate() }
                
                <DividerSection sectionText={settings?.benefitciarySelectionSectionTitle} >
					<ContentSpacer>
						{ RenderPreretirment() }
						{ RenderCpfLife() }
						{ RenderLayLumpSum() }
					</ContentSpacer>
					{
						beneficiaryLandingPageModel?.beneficiaryData && beneficiaryLandingPageModel?.beneficiaryData?.has_group_life &&
						<DividerSection >
							<ContentSpacer>
								<div className={labelSupplementClasses.labelLayout}>
									<h3>{settings?.groupLifeInsuranceSectionTitle}</h3>

								</div>
							</ContentSpacer>
							<ContentSpacer>
								<Banner
									banner={{
										type: 'warning',
										description: <WysiwygWithReactLink content={settings?.groupLifeInsuranceWarningMessage} />,
									}} />
							</ContentSpacer>
							<ContentSpacer>
								<h4>{settings?.groupLifeDownloadFormTitle}</h4>
								<a className={buttonClasses.linkButton} href={settings?.groupLifeDownloadFormLink} target='_blank' rel="noreferrer"><PictureAsPdfIcon />{settings?.groupLifeDownloadFormLinkText}<GetApp /></a>
							</ContentSpacer>
							<ContentSpacer>
								<h4>{settings?.groupLifeUploadFormTitle}</h4>
								<a className={buttonClasses.linkButton} href={settings?.groupLifeUploadFormLink} target='_blank' rel="noreferrer">{settings?.groupLifeUploadFormLinkText}<PublishIcon /></a>
							</ContentSpacer>
							<WysiwygWithReactLink content={settings?.groupLifeDescription} />
						</DividerSection>
					}
					{
						beneficiaryLandingPageModel?.beneficiaryData && beneficiaryLandingPageModel?.beneficiaryData?.has_supplemental_life &&
						<DividerSection>
							<ContentSpacer>
								<div className={labelSupplementClasses.labelLayout}>
									<h3>{settings?.supplementLifeInsuranceSectionTitle}</h3>

								</div>
							</ContentSpacer>
							<ContentSpacer>
								<Banner
									banner={{
										type: 'warning',
										description: <WysiwygWithReactLink content={settings?.supplementLifeInsuranceWarningMessage} />,
									}} />
							</ContentSpacer>
							<ContentSpacer>
								<h4>{settings?.supplementLifeDownloadFormTitle}</h4>
								<a className={buttonClasses.linkButton} href={settings?.supplementLifeDownloadFormLink} target='_blank' rel="noreferrer" ><PictureAsPdfIcon />{settings?.supplementLifeDownloadFormLinkText}<GetApp /></a>
							</ContentSpacer>
							<ContentSpacer>
								<h4>{settings?.supplementLifeUploadFormTitle}</h4>
								<a className={buttonClasses.linkButton} href={settings?.supplementLifeUploadFormLink} target='_blank' rel="noreferrer">{settings?.supplementLifeUploadFormLinkText}<PublishIcon /></a>
							</ContentSpacer>
							<WysiwygWithReactLink content={settings?.supplementLifeDescription} />
						</DividerSection>
					}

					{(settings?.benefitciaryChildBenefitPlanTitle || settings?.benefitciaryChildBenefitPlanDescription) && preretirmentData ?
						<ContentSpacer>
							<div className={labelSupplementClasses.labelLayout}>
								<h3>{settings?.benefitciaryChildBenefitPlanTitle}</h3>
							</div>
							<WysiwygWithReactLink content={settings?.benefitciaryChildBenefitPlanDescription} />
						</ContentSpacer>
						: null}

				</DividerSection>

			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<div className='sidebar'>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</div>
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
}

export default BeneficiaryLandingPageView;